// @refresh reset
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useApp, apiFetch } from '../../context/AppContext';
import {
  Bot, PenLine, Save, Undo2, RotateCcw, ArrowLeft,
  CheckCircle2, CircleDot, Pencil, Type, Code2, Minus, Plus,
  Coins, Lightbulb, Activity, Loader2, TrendingUp, Wallet, Gauge,
  FileText, Layers, History, Inbox, Sparkles, Building2, TextAlignStart,
  ListOrdered, Files, Braces, RefreshCw, ClipboardList, Percent, Hash,
} from 'lucide-react';
import { formatTokenCount } from '../../utils/policyTokenUtils';

const DEFAULT_SENTENCE_COUNT = 4;
const MIN_SENTENCE_COUNT = 2;
const MAX_SENTENCE_COUNT = 12;

const FEATURE_LABELS = {
  policy_generation: 'Policy generation',
  policy_generation_retry: 'JSON retry',
  reference_evidence: 'Reference evidence',
};

const FEATURE_ICONS = {
  policy_generation: FileText,
  policy_generation_retry: RefreshCw,
  reference_evidence: ClipboardList,
};

const TIP_ICONS = [TextAlignStart, ListOrdered, Files, Braces];

function estimatePromptTokens(text) {
  const s = String(text || '').trim();
  if (!s) return 0;
  return Math.ceil(s.length / 4);
}

function formatFeature(feature) {
  return FEATURE_LABELS[feature] || String(feature || '').replace(/_/g, ' ');
}

function FeatureIcon({ feature, size = 12 }) {
  const Icon = FEATURE_ICONS[feature] || Coins;
  return <Icon size={size} aria-hidden="true" />;
}

function formatEventTime(value) {
  if (!value) return '';
  try {
    return new Date(value).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

const EMPTY_TOKEN_USAGE = {
  quota: 50000,
  used: 0,
  remaining: 50000,
  percent_used: 0,
  alert_percent: 80,
  estimate: { prompt_tokens: 0, all_policies_tokens: 0, policies: [] },
  feature_totals: [],
  recent: [],
  tips: [
    'Shorter system prompts use fewer input tokens on every generation.',
    'Lower “Sentences per section” cuts output tokens the most.',
    'Generate one policy at a time instead of all 10 when the budget is low.',
    'Retries (when JSON is invalid) consume tokens again — keep the prompt asking for JSON only.',
  ],
};

function TenantPolicyPromptPage() {
  const { showNotification } = useApp();

  const [systemPrompt, setSystemPrompt] = useState('');
  const [savedPrompt, setSavedPrompt] = useState('');
  const [defaultSystem, setDefaultSystem] = useState('');
  const [sentenceCount, setSentenceCount] = useState(DEFAULT_SENTENCE_COUNT);
  const [savedSentenceCount, setSavedSentenceCount] = useState(DEFAULT_SENTENCE_COUNT);
  const [minSentences, setMinSentences] = useState(MIN_SENTENCE_COUNT);
  const [maxSentences, setMaxSentences] = useState(MAX_SENTENCE_COUNT);
  const [isCustom, setIsCustom] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [loadingDefault, setLoadingDefault] = useState(false);
  const [tokenUsage, setTokenUsage] = useState(EMPTY_TOKEN_USAGE);
  const [tokensLoading, setTokensLoading] = useState(true);

  const applyPayload = useCallback((data) => {
    const next = data.system_prompt || '';
    const nextCount = Number(data.sentence_count) || DEFAULT_SENTENCE_COUNT;
    setSystemPrompt(next);
    setSavedPrompt(next);
    setDefaultSystem(data.default_system_prompt || '');
    setSentenceCount(nextCount);
    setSavedSentenceCount(nextCount);
    setMinSentences(Number(data.min_sentence_count) || MIN_SENTENCE_COUNT);
    setMaxSentences(Number(data.max_sentence_count) || MAX_SENTENCE_COUNT);
    setIsCustom(Boolean(data.is_custom));
  }, []);

  const loadTokenUsage = useCallback(async () => {
    try {
      const res = await apiFetch('/tenant/settings/ai-tokens');
      if (!res.ok) {
        setTokenUsage(EMPTY_TOKEN_USAGE);
        return;
      }
      const data = await res.json();
      setTokenUsage(data && Number(data.quota) > 0 ? data : EMPTY_TOKEN_USAGE);
    } catch {
      setTokenUsage(EMPTY_TOKEN_USAGE);
    } finally {
      setTokensLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch('/tenant/settings/policy-prompt');
        if (!res.ok) {
          showNotification('Failed to load policy generation prompt', 'error');
          return;
        }
        const data = await res.json();
        if (!cancelled) applyPayload(data);
      } catch {
        if (!cancelled) showNotification('Network error loading prompt', 'error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [applyPayload, showNotification]);

  useEffect(() => {
    loadTokenUsage();
  }, [loadTokenUsage]);

  const dirty = systemPrompt !== savedPrompt || sentenceCount !== savedSentenceCount;
  const busy = saving || resetting || loadingDefault;
  const charCount = (systemPrompt || '').length;

  const liveEstimates = useMemo(() => {
    const policies = tokenUsage?.estimate?.policies || [];
    const promptTokens = estimatePromptTokens(systemPrompt) + 180;
    const rows = policies.map((policy) => ({
      ...policy,
      tokens: promptTokens + sentenceCount * Math.max(Number(policy.sections) || 1, 1) * 28,
    }));
    const allPoliciesTokens = rows.reduce((sum, row) => sum + row.tokens, 0);
    const remaining = Math.max(0, Number(tokenUsage?.remaining) || 0);
    const avg = rows.length ? Math.round(allPoliciesTokens / rows.length) : 0;
    return {
      promptTokens,
      rows,
      allPoliciesTokens,
      avg,
      affordableCount: avg > 0 ? Math.floor(remaining / avg) : 0,
    };
  }, [sentenceCount, systemPrompt, tokenUsage]);

  const adjustSentenceCount = (delta) => {
    setSentenceCount((current) => Math.min(maxSentences, Math.max(minSentences, current + delta)));
  };

  const handleSave = useCallback(async () => {
    if (!systemPrompt.trim()) {
      showNotification('System prompt cannot be empty', 'error');
      return;
    }
    setSaving(true);
    try {
      const res = await apiFetch('/tenant/settings/policy-prompt', {
        method: 'PATCH',
        body: JSON.stringify({
          system_prompt: systemPrompt,
          sentence_count: sentenceCount,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        applyPayload(data);
        showNotification('Prompt settings saved. New policy generations will use them.');
      } else {
        const err = await res.json().catch(() => ({}));
        showNotification(err.detail || 'Failed to update prompt', 'error');
      }
    } catch {
      showNotification('Network error saving prompt', 'error');
    } finally {
      setSaving(false);
    }
  }, [applyPayload, sentenceCount, showNotification, systemPrompt]);

  const handleLoadDefaults = useCallback(async () => {
    setLoadingDefault(true);
    try {
      const res = await apiFetch('/tenant/settings/policy-prompt');
      if (!res.ok) {
        showNotification('Failed to load the default system prompt', 'error');
        return;
      }
      const data = await res.json();
      const nextDefault = data.default_system_prompt || '';
      setDefaultSystem(nextDefault);
      const alreadyShowing = nextDefault === systemPrompt;
      setSystemPrompt(nextDefault);
      showNotification(
        alreadyShowing
          ? 'Editor already matches the default prompt.'
          : 'Loaded the default system prompt. Save to apply.',
        'info',
      );
    } catch {
      showNotification('Network error loading default prompt', 'error');
    } finally {
      setLoadingDefault(false);
    }
  }, [showNotification, systemPrompt]);

  const handleResetToDefault = useCallback(async () => {
    setResetting(true);
    try {
      const res = await apiFetch('/tenant/settings/policy-prompt', {
        method: 'PATCH',
        body: JSON.stringify({ reset_to_default: true }),
      });
      if (res.ok) {
        const data = await res.json();
        const wasCustom = isCustom || dirty;
        applyPayload(data);
        showNotification(
          wasCustom
            ? 'Reset to the default prompt and sentence count.'
            : 'Already using the default settings.',
        );
      } else {
        const err = await res.json().catch(() => ({}));
        showNotification(err.detail || 'Failed to reset prompt', 'error');
      }
    } catch {
      showNotification('Network error resetting prompt', 'error');
    } finally {
      setResetting(false);
    }
  }, [applyPayload, dirty, isCustom, showNotification]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-slate-400 text-sm">
        <Loader2 size={20} className="animate-spin text-indigo-600 mr-2" aria-hidden="true" />
        Loading policy generation prompt…
      </div>
    );
  }

  const statusChip = dirty
    ? { label: 'Unsaved', className: 'bg-amber-50 text-amber-800 border border-amber-200', icon: Pencil }
    : isCustom
      ? { label: 'Custom', className: 'bg-indigo-50 text-indigo-800 border border-indigo-200', icon: CheckCircle2 }
      : { label: 'Default', className: 'bg-white text-indigo-700 border border-indigo-200', icon: CircleDot };

  const StatusIcon = statusChip.icon;

  return (
    <div className="p-1 space-y-6 min-h-screen animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-indigo-700 to-indigo-900 flex items-center justify-center shadow-lg">
            <Bot size={20} className="text-white" aria-hidden="true" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Policy Generation Prompt</h1>
            <p className="text-sm text-slate-500 mt-0.5 inline-flex items-center gap-1.5">
              <Sparkles size={13} className="text-indigo-600 flex-shrink-0" aria-hidden="true" />
              System instructions used for this organisation's AI-generated DPDP policies
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2.5">
          <button
            type="button"
            onClick={handleLoadDefaults}
            disabled={busy}
            className="inline-flex items-center justify-center gap-2 h-9 px-4 rounded-xl text-sm font-semibold bg-white text-indigo-800 border border-indigo-200 hover:bg-indigo-50 hover:border-indigo-300 shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 focus-visible:ring-offset-1 transition-colors disabled:opacity-45 disabled:cursor-not-allowed disabled:hover:bg-white"
          >
            {loadingDefault ? (
              <Loader2 size={14} className="animate-spin" aria-hidden="true" />
            ) : (
              <RotateCcw size={14} aria-hidden="true" />
            )}
            {loadingDefault ? 'Loading…' : 'Load Default'}
          </button>
          <button
            type="button"
            onClick={handleResetToDefault}
            disabled={busy}
            className="inline-flex items-center justify-center gap-2 h-9 px-4 rounded-xl text-sm font-semibold bg-white text-slate-700 border border-slate-200 hover:bg-slate-50 hover:border-slate-300 shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-1 transition-colors disabled:opacity-45 disabled:cursor-not-allowed disabled:hover:bg-white"
          >
            {resetting ? (
              <Loader2 size={14} className="animate-spin" aria-hidden="true" />
            ) : (
              <Undo2 size={14} aria-hidden="true" />
            )}
            {resetting ? 'Resetting…' : 'Reset'}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={busy || !dirty}
            className="inline-flex items-center justify-center gap-2 h-9 px-4 rounded-xl text-sm font-semibold bg-indigo-700 text-white hover:bg-indigo-800 shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-1 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-indigo-700 disabled:shadow-none"
          >
            {saving ? (
              <Loader2 size={14} className="animate-spin" aria-hidden="true" />
            ) : (
              <Save size={14} aria-hidden="true" />
            )}
            {saving ? 'Saving…' : 'Save Prompt'}
          </button>
        </div>
      </div>

      <section
        className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden"
        aria-labelledby="system-prompt-heading"
      >
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 px-6 py-5 bg-indigo-50 border-b border-indigo-100">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-700 to-indigo-900 text-white flex items-center justify-center flex-shrink-0 shadow-sm">
              <PenLine size={16} aria-hidden="true" />
            </span>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h2 id="system-prompt-heading" className="text-base font-semibold text-slate-900">
                  System prompt
                </h2>
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${statusChip.className}`}>
                  {StatusIcon ? <StatusIcon size={11} aria-hidden="true" /> : null}
                  {statusChip.label}
                </span>
              </div>
              <p className="text-sm text-slate-500 mt-1 inline-flex items-center gap-1.5">
                <Building2 size={13} className="text-indigo-600 flex-shrink-0" aria-hidden="true" />
                Applies only to this organisation
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 self-start lg:self-center">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 inline-flex items-center gap-1.5">
                <ListOrdered size={12} className="text-indigo-700" aria-hidden="true" />
                Sentences per section
              </p>
              <p className="text-[11px] text-slate-400 mt-0.5 inline-flex items-center gap-1">
                <TextAlignStart size={11} aria-hidden="true" />
                Longer number = longer policy clauses
              </p>
            </div>
            <div className="inline-flex items-center rounded-xl border border-indigo-200 bg-white overflow-hidden shadow-sm">
              <button
                type="button"
                onClick={() => adjustSentenceCount(-1)}
                disabled={busy || sentenceCount <= minSentences}
                className="h-9 w-9 flex items-center justify-center text-indigo-700 hover:bg-indigo-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                aria-label="Fewer sentences"
              >
                <Minus size={14} aria-hidden="true" />
              </button>
              <input
                id="sentence-count"
                type="number"
                min={minSentences}
                max={maxSentences}
                value={sentenceCount}
                onChange={(e) => {
                  const next = Number(e.target.value);
                  if (Number.isNaN(next)) return;
                  setSentenceCount(Math.min(maxSentences, Math.max(minSentences, next)));
                }}
                className="w-12 h-9 text-center text-sm font-semibold tabular-nums text-slate-900 bg-transparent border-x border-indigo-100 focus:outline-none"
                aria-label="Number of sentences per section"
              />
              <button
                type="button"
                onClick={() => adjustSentenceCount(1)}
                disabled={busy || sentenceCount >= maxSentences}
                className="h-9 w-9 flex items-center justify-center text-indigo-700 hover:bg-indigo-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                aria-label="More sentences"
              >
                <Plus size={14} aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
        <div className="px-6 py-5 flex flex-col bg-white">
          <label htmlFor="system-prompt-editor" className="sr-only">
            System prompt
          </label>
          <div className="flex flex-col min-h-[18rem] rounded-2xl border border-indigo-100 bg-white overflow-hidden focus-within:border-indigo-300 focus-within:ring-2 focus-within:ring-indigo-100 transition-all">
            <div className="flex items-center justify-between px-3.5 py-2 bg-indigo-50 border-b border-indigo-100">
              <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-indigo-800">
                <Code2 size={12} aria-hidden="true" />
                Editor
              </span>
              <span className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-500 tabular-nums">
                <Type size={11} aria-hidden="true" />
                {charCount.toLocaleString()}
              </span>
            </div>
            <textarea
              id="system-prompt-editor"
              rows={16}
              value={systemPrompt}
              onChange={(e) => setSystemPrompt(e.target.value)}
              spellCheck={false}
              className="w-full min-h-[18rem] px-4 py-3.5 text-[13px] leading-6 font-mono text-slate-800 bg-white resize-y focus:outline-none placeholder:text-slate-400"
              placeholder="Describe how the model should write DPDP policies…"
            />
          </div>
          <p className="text-xs text-slate-500 mt-3 leading-relaxed inline-flex items-start gap-1.5">
            <ListOrdered size={13} className="text-indigo-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
            <span>
              Each generated section will be{' '}
              <span className="font-semibold text-indigo-800">{sentenceCount} sentences</span>
              . The user prompt stays on the built-in default.
            </span>
          </p>
        </div>
      </section>

      <section
        className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden"
        aria-labelledby="token-usage-heading"
      >
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 px-6 py-5 bg-indigo-50 border-b border-indigo-100">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-700 to-indigo-900 text-white flex items-center justify-center flex-shrink-0 shadow-sm">
              <Coins size={16} aria-hidden="true" />
            </span>
            <div>
              <h2 id="token-usage-heading" className="text-base font-semibold text-slate-900">
                Token usage
              </h2>
              <p className="text-sm text-slate-500 mt-1 inline-flex items-center gap-1.5">
                <Gauge size={13} className="text-indigo-600 flex-shrink-0" aria-hidden="true" />
                Organisation budget for AI policy generation and related calls
              </p>
            </div>
          </div>
          {tokensLoading ? (
            <span className="inline-flex items-center gap-2 text-sm text-slate-500">
              <Loader2 size={14} className="animate-spin text-indigo-500" aria-hidden="true" />
              Loading usage…
            </span>
          ) : null}
        </div>

        <div className="px-6 py-5 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 px-4 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 inline-flex items-center gap-1.5">
                <TrendingUp size={12} className="text-indigo-700" aria-hidden="true" />
                Tokens used
              </p>
              <p className="mt-1 text-2xl font-bold tabular-nums text-slate-900">
                {formatTokenCount(tokenUsage.used)}
              </p>
            </div>
            <div className="rounded-xl border border-emerald-100 bg-emerald-50/70 px-4 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 inline-flex items-center gap-1.5">
                <Wallet size={12} className="text-emerald-700" aria-hidden="true" />
                Tokens left
              </p>
              <p className="mt-1 text-2xl font-bold tabular-nums text-emerald-800">
                {formatTokenCount(tokenUsage.remaining)}
              </p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 inline-flex items-center gap-1.5">
                <Gauge size={12} className="text-slate-600" aria-hidden="true" />
                Organisation quota
              </p>
              <p className="mt-1 text-2xl font-bold tabular-nums text-slate-900">
                {formatTokenCount(tokenUsage.quota)}
              </p>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between text-xs text-slate-500 mb-1.5">
              <span className="inline-flex items-center gap-1">
                <Percent size={11} aria-hidden="true" />
                {tokenUsage.percent_used}% of quota used
                {tokenUsage.alert_percent ? ` · alert at ${tokenUsage.alert_percent}%` : ''}
              </span>
              <span className="inline-flex items-center gap-1">
                <Wallet size={11} aria-hidden="true" />
                {formatTokenCount(tokenUsage.remaining)} remaining
              </span>
            </div>
                <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
                  <div
                    className={`h-full rounded-full ${tokenUsage.percent_used >= 90 ? 'bg-rose-500' : tokenUsage.percent_used >= 70 ? 'bg-amber-500' : 'bg-indigo-600'}`}
                    style={{ width: `${Math.min(100, tokenUsage.percent_used)}%` }}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Activity size={14} className="text-indigo-700" aria-hidden="true" />
                    <h3 className="text-sm font-semibold text-slate-900">What uses tokens</h3>
                  </div>
                  <p className="text-xs text-slate-500 mb-3 inline-flex items-start gap-1.5">
                    <Hash size={12} className="text-indigo-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
                    <span>
                      Live estimate at <span className="font-semibold text-indigo-800">{sentenceCount} sentences</span> per section.
                      Input prompt ≈ {formatTokenCount(liveEstimates.promptTokens)} tokens.
                    </span>
                  </p>
                  <div className="rounded-xl border border-slate-200 overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                        <tr>
                          <th className="text-left font-semibold px-3 py-2">
                            <span className="inline-flex items-center gap-1.5">
                              <FileText size={11} aria-hidden="true" />
                              Policy
                            </span>
                          </th>
                          <th className="text-right font-semibold px-3 py-2">
                            <span className="inline-flex items-center justify-end gap-1.5">
                              <Layers size={11} aria-hidden="true" />
                              Sections
                            </span>
                          </th>
                          <th className="text-right font-semibold px-3 py-2">
                            <span className="inline-flex items-center justify-end gap-1.5">
                              <Coins size={11} aria-hidden="true" />
                              Est. tokens
                            </span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {liveEstimates.rows.map((row) => (
                          <tr key={row.id} className="border-t border-slate-100">
                            <td className="px-3 py-2 text-slate-800">
                              <span className="inline-flex items-center gap-2">
                                <FileText size={13} className="text-indigo-600 flex-shrink-0" aria-hidden="true" />
                                {row.title}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums text-slate-500">{row.sections}</td>
                            <td className="px-3 py-2 text-right tabular-nums font-medium text-indigo-800">
                              {formatTokenCount(row.tokens)}
                            </td>
                          </tr>
                        ))}
                        <tr className="border-t border-slate-200 bg-indigo-50/50">
                          <td className="px-3 py-2 font-semibold text-slate-900" colSpan={2}>
                            <span className="inline-flex items-center gap-2">
                              <Layers size={13} className="text-indigo-700" aria-hidden="true" />
                              All catalogue policies
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums font-bold text-indigo-900">
                            {formatTokenCount(liveEstimates.allPoliciesTokens)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-slate-500 mt-2 inline-flex items-start gap-1.5">
                    <Sparkles size={12} className="text-indigo-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
                    <span>
                      With {formatTokenCount(tokenUsage.remaining)} left you can generate about{' '}
                      <span className="font-semibold text-indigo-800">
                        {liveEstimates.affordableCount} {liveEstimates.affordableCount === 1 ? 'policy' : 'policies'}
                      </span>{' '}
                      at the current sentence setting.
                    </span>
                  </p>
                </div>

                <div className="space-y-4">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-900 mb-2 inline-flex items-center gap-2">
                      <History size={14} className="text-indigo-700" aria-hidden="true" />
                      Recent consumption
                    </h3>
                    {tokenUsage.recent?.length ? (
                      <ul className="rounded-xl border border-slate-200 divide-y divide-slate-100">
                        {tokenUsage.recent.map((event) => (
                          <li key={event.id} className="px-3 py-2.5 flex items-start justify-between gap-3">
                            <div className="min-w-0 flex items-start gap-2">
                              <span className="mt-0.5 w-7 h-7 rounded-lg bg-indigo-50 text-indigo-700 flex items-center justify-center flex-shrink-0">
                                <FeatureIcon feature={event.feature} size={13} />
                              </span>
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-slate-800 truncate">{event.label}</p>
                                <p className="text-[11px] text-slate-500 mt-0.5">
                                  {formatFeature(event.feature)}
                                  {event.prompt_tokens || event.completion_tokens
                                    ? ` · in ${formatTokenCount(event.prompt_tokens)} / out ${formatTokenCount(event.completion_tokens)}`
                                    : ''}
                                  {event.created_at ? ` · ${formatEventTime(event.created_at)}` : ''}
                                </p>
                              </div>
                            </div>
                            <span className="text-sm font-semibold tabular-nums text-indigo-800 flex-shrink-0 inline-flex items-center gap-1">
                              <Coins size={12} aria-hidden="true" />
                              {formatTokenCount(event.tokens)}
                            </span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm text-slate-500 rounded-xl border border-dashed border-slate-200 px-3 py-4 inline-flex items-start gap-2 w-full">
                        <Inbox size={16} className="text-indigo-500 mt-0.5 flex-shrink-0" aria-hidden="true" />
                        No AI generations recorded yet. Usage appears here after Policy Store generate.
                      </p>
                    )}
                    {tokenUsage.feature_totals?.length ? (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {tokenUsage.feature_totals.map((item) => (
                          <span
                            key={item.feature}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium bg-indigo-50 text-indigo-800 border border-indigo-100"
                          >
                            <FeatureIcon feature={item.feature} size={11} />
                            {formatFeature(item.feature)}
                            <span className="tabular-nums">{formatTokenCount(item.tokens)}</span>
                          </span>
                        ))}
                      </div>
                    ) : null}
                  </div>

                  <div className="rounded-xl border border-indigo-100 bg-indigo-50/50 px-4 py-3">
                    <div className="flex items-center gap-2 mb-2">
                      <Lightbulb size={14} className="text-indigo-700" aria-hidden="true" />
                      <h3 className="text-sm font-semibold text-slate-900">How to use remaining tokens</h3>
                    </div>
                    <ul className="space-y-1.5 text-sm text-slate-600">
                      {(tokenUsage.tips || []).map((tip, index) => {
                        const TipIcon = TIP_ICONS[index] || Lightbulb;
                        return (
                          <li key={tip} className="flex gap-2">
                            <TipIcon size={14} className="mt-0.5 text-indigo-600 flex-shrink-0" aria-hidden="true" />
                            <span>{tip}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>
              </div>
        </div>
      </section>

      <Link
        to="/tenant/settings"
        className="inline-flex items-center gap-1.5 text-sm font-medium text-indigo-600 hover:text-violet-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 rounded-md transition-colors"
      >
        <ArrowLeft size={14} aria-hidden="true" />
        Back to Settings
      </Link>
    </div>
  );
}

export default TenantPolicyPromptPage;
